import "./TriviaLayout.scss";

import { useEffect, useState } from "react";

import { IDynamicForm, ButtonFormItem } from "config";
import { useButtonAction } from "containers/DynamicForm/useButtonAction";
import { useRetryNextForm } from "containers/DynamicForm/useButtonCommand/useRetryNextForm";
import TriviaContainer from "containers/Trivia/Trivia";
import { PollingStatus } from "modules/hooks/usePolling";
import { useAppDispatch } from "store";
import { getProductOfferings } from "store/actions/product-offerings";

import { find } from "lodash-es";

interface IProps {
  items: IDynamicForm["items"];
  slug: IDynamicForm["slug"];
}

const TIMEOUT_BUTTON_ACTION_KEY = "underwriting-results-button-request-timeout";

export function TriviaLayout({ items }: IProps) {
  //HOOKS
  const dispatch = useAppDispatch();
  const retryNextFormCommand = useRetryNextForm();

  // LOCAL STATE
  const [pollingStatus, setPollingStatus] = useState<PollingStatus>("polling");

  // LOCAL DERIVED VARIABLES
  const underwritingResultsButtonItem = items.find(
    dynamicFormItem => dynamicFormItem.key === "underwriting-results-button"
  ) as ButtonFormItem;

  const underwritingResultsButtonActions = useButtonAction(
    underwritingResultsButtonItem?.content?.actions || []
  );

  // Timeout-Related Button Item Actions
  const underwritingTimeoutButtonItem = items.find(
    dynamicFormItem => dynamicFormItem.key === TIMEOUT_BUTTON_ACTION_KEY
  ) as ButtonFormItem;

  const underwritingTimeoutButtonActions = useButtonAction(
    underwritingTimeoutButtonItem?.content?.actions || []
  );

  // METHODS

  const completeTriviaCallback = async () => {
    if (pollingStatus === "timed-out") {
      await underwritingTimeoutButtonActions();
    } else {
      await underwritingResultsButtonActions();
    }
  };

  const onComplete = async () => {
    try {
      dispatch(getProductOfferings());
      await completeTriviaCallback();
    } catch (error) {
      return error;
    }
  };

  // MOUNT EFFECTS
  useEffect(() => {
    const pollNextForm = async () => {
      const nextFormRetryAction = find(underwritingResultsButtonItem?.content?.actions, {
        command: { arguments: [{ name: "retryTimeout" }], destination: "nextForm" },
      });

      try {
        if (nextFormRetryAction) {
          await retryNextFormCommand(nextFormRetryAction.command.arguments || [], {});
        }
        setPollingStatus("done");
      } catch {
        setPollingStatus("timed-out");
      }
    };

    pollNextForm();
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TriviaContainer
      isFailedQuote={pollingStatus === "timed-out"}
      isQuoteReady={pollingStatus !== "polling"}
      onComplete={onComplete}
      requestStatus={pollingStatus}
    />
  );
}
